import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { qk } from '@/lib/queryKeys.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table.jsx';
import { FormField, Input, DateInput } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatLKR, formatDate, periodLabel, centsToRupees, rupeesToCents, getCurrencySymbol } from '@/lib/format.js';
import { Plus, Trash2, Pencil, Download, Printer } from 'lucide-react';

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [comp, setComp] = useState({ component_id: '', amount: 0, percent: 0, effective_from: `${new Date().getFullYear()}-01-01`, effective_to: '' });

  const { data: summary, isLoading } = useQuery({
    queryKey: ['employeeSummary', id],
    queryFn: () => api.get(`/employees/${id}/summary`),
  });
  const { data: emp } = useQuery({ queryKey: ['employee', id], queryFn: () => api.get(`/employees/${id}`) });
  const { data: components = [] } = useQuery({ queryKey: ['salaryComponents'], queryFn: () => api.get('/salary-components') });

  const addComp = useMutation({
    mutationFn: () => api.post(`/employees/${id}/components`, {
      component_id: Number(comp.component_id),
      amount_cents: rupeesToCents(comp.amount),
      percent: Number(comp.percent || 0),
      effective_from: comp.effective_from,
      effective_to: comp.effective_to || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee', id] }); setShowAdd(false); setEditTarget(null); toast({ variant: 'success', title: 'Component added' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const removeComp = useMutation({
    mutationFn: (compId) => api.del(`/employees/${id}/components/${compId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee', id] }); toast({ variant: 'success', title: 'Removed' }); },
  });
  const editComp = useMutation({
    mutationFn: () => api.put(`/employees/${id}/components/${editTarget}`, {
      amount_cents: rupeesToCents(comp.amount),
      percent: Number(comp.percent || 0),
      effective_from: comp.effective_from,
      effective_to: comp.effective_to || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employee', id] }); setShowAdd(false); setEditTarget(null); toast({ variant: 'success', title: 'Component updated' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const openEdit = (c) => {
    setEditTarget(c.id);
    setComp({ component_id: c.component_id, amount: centsToRupees(c.amount_cents), percent: c.percent || 0, effective_from: c.effective_from?.slice(0, 10), effective_to: c.effective_to?.slice(0, 10) || '' });
    setShowAdd(true);
  };

  const handlePrintPayslip = async (payslipId) => {
    try {
      const htmlUrl = await api.blob(`/payslips/item/${payslipId}/html`);
      const printWindow = window.open(htmlUrl, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
    } catch (e) {
      toast({ variant: 'error', title: 'Print failed', description: e.message });
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('photo', file);
    await api.post(`/employees/${id}/photo`, fd, { headers: {} });
    qc.invalidateQueries({ queryKey: ['employee', id] });
    toast({ variant: 'success', title: 'Photo uploaded' });
  };

  if (isLoading || !emp) return <div className="p-4">Loading…</div>;

  return (
    <div className="space-y-5">
      <PageHeader
        title={emp.full_name}
        description={`${emp.emp_no} · ${emp.department_name || '—'} · ${emp.designation_name || '—'}`}
        actions={
          <>
            <Button asChild variant="outline"><Link to={`/employees/${id}/edit`}><Pencil className="h-4 w-4" />Edit</Link></Button>
            <Badge variant={emp.status === 'active' ? 'success' : 'warning'}>{emp.status}</Badge>
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-3">
        <Section title="Profile" className="lg:col-span-1">
          <div className="flex flex-col items-center gap-3">
            {emp.photo_path ? <img src={emp.photo_path} className="h-24 w-24 rounded-full object-cover border" /> : <div className="h-24 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />}
            <input type="file" accept="image/*" onChange={uploadPhoto} className="text-xs" />
            <dl className="w-full mt-3 grid grid-cols-2 gap-y-1 text-sm">
              <dt className="text-slate-500">NIC</dt><dd>{emp.nic || '—'}</dd>
              <dt className="text-slate-500">Email</dt><dd className="break-all">{emp.email || '—'}</dd>
              <dt className="text-slate-500">Contact</dt><dd>{emp.contact || '—'}</dd>
              <dt className="text-slate-500">Joined</dt><dd>{formatDate(emp.join_date)}</dd>
              <dt className="text-slate-500">Type</dt><dd>{emp.employment_type}</dd>
              <dt className="text-slate-500">Basic</dt><dd className="font-semibold">{formatLKR(emp.basic_salary_cents)}</dd>
              <dt className="text-slate-500">EPF #</dt><dd>{emp.epf_member_no || '—'}</dd>
              <dt className="text-slate-500">Bank</dt><dd>{[emp.bank_name, emp.bank_account].filter(Boolean).join(' · ') || '—'}</dd>
            </dl>
          </div>
        </Section>

        <div className="lg:col-span-2">
          <Tabs defaultValue="components">
            <TabsList>
              <TabsTrigger value="components">Salary Components</TabsTrigger>
              <TabsTrigger value="leaves">Leave Balances</TabsTrigger>
              <TabsTrigger value="loans">Loans & Advances</TabsTrigger>
              <TabsTrigger value="holds">Salary Holds</TabsTrigger>
              <TabsTrigger value="payslips">Recent Payslips</TabsTrigger>
            </TabsList>

            <TabsContent value="components">
              <Section title="Salary Components" actions={<Button onClick={() => setShowAdd(true)}><Plus className="h-4 w-4" />Add</Button>}>
                <Table>
                  <THead><TR><TH>Code</TH><TH>Name</TH><TH>Type</TH><TH className="text-right">Amount</TH><TH className="text-right">%</TH><TH>From</TH><TH>To</TH><TH /></TR></THead>
                  <TBody>
                    {(emp.components || []).length === 0 ? (
                      <TR><TD colSpan={8} className="text-center text-slate-500 py-4">No components assigned</TD></TR>
                    ) : emp.components.map((c) => (
                      <TR key={c.id}>
                        <TD className="font-mono">{c.code}</TD>
                        <TD>{c.name}</TD>
                        <TD><Badge variant={c.type === 'allowance' ? 'success' : 'warning'}>{c.type}</Badge></TD>
                        <TD className="text-right tabular-nums">{formatLKR(c.amount_cents)}</TD>
                        <TD className="text-right tabular-nums">{c.percent || 0}%</TD>
                        <TD>{formatDate(c.effective_from)}</TD>
                        <TD>{c.effective_to ? formatDate(c.effective_to) : '—'}</TD>
                        <TD className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(c)}><Pencil className="h-4 w-4 text-blue-500" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => removeComp.mutate(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </Section>
            </TabsContent>

            <TabsContent value="leaves">
              <Section title={`Leave Balances · ${new Date().getFullYear()}`}>
                <Table>
                  <THead><TR><TH>Code</TH><TH>Name</TH><TH className="text-right">Opening</TH><TH className="text-right">Used</TH><TH className="text-right">Balance</TH></TR></THead>
                  <TBody>
                    {(summary?.leaves || []).length === 0 ? (
                      <TR><TD colSpan={5} className="text-center text-slate-500 py-4">No balances yet</TD></TR>
                    ) : summary.leaves.map((l) => (
                      <TR key={l.code}><TD className="font-mono">{l.code}</TD><TD>{l.name}</TD><TD className="text-right">{l.opening}</TD><TD className="text-right">{l.used}</TD><TD className="text-right font-semibold">{(l.opening - l.used).toFixed(1)}</TD></TR>
                    ))}
                  </TBody>
                </Table>
              </Section>
            </TabsContent>

            <TabsContent value="loans">
              <Section title="Active Loans">
                <Table>
                  <THead><TR><TH>Principal</TH><TH>Balance</TH><TH>Installments</TH><TH>Status</TH></TR></THead>
                  <TBody>
                    {(summary?.loans || []).length === 0 ? <TR><TD colSpan={4} className="text-center text-slate-500 py-4">No active loans</TD></TR> :
                      summary.loans.map((l) => <TR key={l.id}><TD>{formatLKR(l.principal_cents)}</TD><TD>{formatLKR(l.balance_cents)}</TD><TD>{l.installments}</TD><TD>{l.status}</TD></TR>)}
                  </TBody>
                </Table>
              </Section>
              <Section title="Active Advances" className="mt-4">
                <Table>
                  <THead><TR><TH>Amount</TH><TH>Balance</TH><TH>Months</TH><TH>Status</TH></TR></THead>
                  <TBody>
                    {(summary?.advances || []).length === 0 ? <TR><TD colSpan={4} className="text-center text-slate-500 py-4">No active advances</TD></TR> :
                      summary.advances.map((a) => <TR key={a.id}><TD>{formatLKR(a.amount_cents)}</TD><TD>{formatLKR(a.balance_cents)}</TD><TD>{a.months}</TD><TD>{a.status}</TD></TR>)}
                  </TBody>
                </Table>
              </Section>
            </TabsContent>

            <TabsContent value="holds">
              <Section title="Salary Holds">
                <Table>
                  <THead><TR><TH>Status</TH><TH>Start Period</TH><TH>End Period</TH><TH>Reason</TH><TH className="text-right">Accumulated Held</TH><TH>Released At</TH></TR></THead>
                  <TBody>
                    {!summary?.salaryHolds || summary.salaryHolds.length === 0 ? (
                      <TR><TD colSpan={6} className="text-center text-slate-500 py-4">No salary holds</TD></TR>
                    ) : (
                      summary.salaryHolds.map((h) => (
                        <TR key={h.id}>
                          <TD><Badge variant={h.status === 'active' ? 'destructive' : 'secondary'}>{h.status === 'active' ? 'ACTIVE' : 'Released'}</Badge></TD>
                          <TD className="text-sm font-mono">{['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][h.start_month]} {h.start_year}</TD>
                          <TD className="text-sm font-mono">{h.end_month ? `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][h.end_month]} ${h.end_year}` : 'Indefinite'}</TD>
                          <TD>{h.reason || '—'}</TD>
                          <TD className="text-right tabular-nums font-mono text-red-600 font-semibold">{formatLKR(h.accumulated_held_cents || 0)}</TD>
                          <TD className="text-sm">{h.released_at ? new Date(h.released_at).toLocaleDateString() : '—'}</TD>
                        </TR>
                      ))
                    )}
                  </TBody>
                </Table>
              </Section>
            </TabsContent>

            <TabsContent value="payslips">
              <Section title="Recent Payslips">
                <Table>
                  <THead><TR><TH>Period</TH><TH>Status</TH><TH className="text-right">Gross</TH><TH className="text-right">Net</TH><TH /></TR></THead>
                  <TBody>
                    {(summary?.payslips || []).length === 0 ? <TR><TD colSpan={5} className="text-center text-slate-500 py-4">No payslips yet</TD></TR> :
                      summary.payslips.map((p) => (
                        <TR key={p.id}>
                          <TD>{periodLabel(p.period_year, p.period_month)}</TD>
                          <TD><Badge variant={p.run_status === 'finalized' || p.run_status === 'paid' ? 'success' : 'warning'}>{p.run_status}</Badge></TD>
                          <TD className="text-right tabular-nums">{formatLKR(p.gross_cents)}</TD>
                          <TD className="text-right tabular-nums">{formatLKR(p.net_pay_cents)}</TD>
                          <TD className="text-right flex gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => api.download(`/payslips/item/${p.id}`, `payslip-${p.period_year}-${p.period_month}.pdf`)}>
                              <Download className="h-4 w-4" />PDF
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handlePrintPayslip(p.id)}>
                              <Printer className="h-4 w-4" />Print
                            </Button>
                          </TD>
                        </TR>
                      ))}
                  </TBody>
                </Table>
              </Section>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={showAdd} onOpenChange={(v) => { setShowAdd(v); if (!v) setEditTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editTarget ? 'Edit Salary Component' : 'Assign Salary Component'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Component" required>
              <Select value={comp.component_id} onChange={(e) => setComp({ ...comp, component_id: e.target.value })} disabled={!!editTarget}>
                <option value="">— select —</option>
                {components.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name} ({c.type})</option>)}
              </Select>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
<FormField label={`Amount (${getCurrencySymbol()})`}><Input type="number" step="0.01" value={comp.amount} onChange={(e) => setComp({ ...comp, amount: e.target.value })} /></FormField>
              <FormField label="Or Percent of Basic"><Input type="number" step="0.01" value={comp.percent} onChange={(e) => setComp({ ...comp, percent: e.target.value })} /></FormField>
              <FormField label="Effective From"><DateInput value={comp.effective_from} onChange={(e) => setComp({ ...comp, effective_from: e.target.value })} /></FormField>
              <FormField label="Effective To (optional)"><DateInput value={comp.effective_to} onChange={(e) => setComp({ ...comp, effective_to: e.target.value })} /></FormField>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => editTarget ? editComp.mutate() : addComp.mutate()} disabled={!comp.component_id || addComp.isPending || editComp.isPending}>{editTarget ? 'Save' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
